"use client";

import Link from "next/link";
import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import ReactLenis, { useLenis } from "lenis/react";
import AnimatedCopy from "@/components/AnimatedCopy/AnimatedCopy";

gsap.registerPlugin(ScrollTrigger, useGSAP);

type WorkItem = {
  id: number;
  title: string;
  category: string;
  image: string;
};

// Keeps ScrollTrigger in step with Lenis smooth scrolling
function LenisSync() {
  useLenis(() => ScrollTrigger.update());
  return null;
}

export default function HomeWorkSection({ workList }: { workList: WorkItem[] }) {
  const wrapperRef = useRef<HTMLElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      const wrapper = wrapperRef.current;
      const header = headerRef.current;
      if (!wrapper || !header) return;

      // Pin the title (position: fixed) from the top of the section until its
      // bottom reaches the bottom of the screen = when the last item has passed.
      // Works even if a parent has overflow hidden, unlike CSS sticky.
      ScrollTrigger.create({
        trigger: wrapper,
        start: "top top",
        end: "bottom bottom",
        pin: header,
        pinSpacing: false,
        anticipatePin: 1,
      });

      // Each item fades/slides in as it rises into view
      gsap.utils.toArray<HTMLElement>(".home-work-item").forEach((item) => {
        gsap.fromTo(
          item,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            ease: "power3.out",
            scrollTrigger: {
              trigger: item,
              start: "top 95%",
              end: "top 65%",
              scrub: true,
            },
          },
        );
      });

      // Positions depend on font-loaded text sizes, so measure again once ready
      document.fonts.ready.then(() => ScrollTrigger.refresh());
    },
    { scope: wrapperRef, dependencies: [workList] }, // auto-cleans on unmount
  );

  return (
    <ReactLenis root>
      <LenisSync />
      <div className="bg-secondary rounded-b-3xl">
        <section ref={wrapperRef} className="relative">
          {/* Pinned title: sits behind the list */}
          <div
            ref={headerRef}
            className="relative z-0 flex h-screen w-full items-center justify-center overflow-hidden"
          >
            <div className="text-center text-8xl font-bold uppercase tracking-widest text-primary/55 sm:hidden">
              <AnimatedCopy
                tag="h1"
                triggerRef={wrapperRef}
                start="top 60%"
                className="primary"
              >
                What We Do Best
              </AnimatedCopy>
            </div>
            <div className="hidden text-center text-8xl font-bold uppercase tracking-widest text-primary/20 sm:block lg:text-[250px]">
              <AnimatedCopy
                tag="h1"
                triggerRef={wrapperRef}
                start="top 60%"
                className="primary"
              >
                Design Develop Deliver
              </AnimatedCopy>
            </div>
          </div>

          {/* List starts one screen down and scrolls OVER the title until the end */}
          <div className="home-work-list relative z-10 -mt-[100vh] pb-[50vh] pt-[100vh] text-white">
            {workList.map((work, index) => (
              <Link
                key={work.id}
                href="/"
                className="home-work-item flex min-h-[60vh] flex-col items-center justify-center gap-3 text-center"
              >
                <p className="primary sm">{`${String(index + 1).padStart(2, "0")} - ${String(workList.length).padStart(2, "0")}`}</p>
                <h2 className="text-3xl font-semibold lg:text-6xl">{work.title}</h2>
                <h4 className="text-xl lg:text-2xl mx-4">{work.category}</h4>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </ReactLenis>
  );
}